/**
 * Community Onboarding Page - Refactored
 *
 * This component has been refactored from a 1,300+ line monolithic component
 * into a clean, maintainable orchestrator using composition architecture.
 */

import React, { useState, useCallback } from 'react';
import {
  useOnboardingFlow,
  useProgressiveDisclosure,
  useTierRecommendation,
} from '@/hooks/onboarding';
import {
  Step1ContactInfo,
  Step2FinancialAssessment,
  Step3CurrentState,
  Step4VisionSolution,
  Step7BudgetTimeline,
  Step8InterestsEngagement,
  Step9Recommendation,
} from '@/components/onboarding/customer/demo-onboarding';
import { CalendarBooking } from '@/components/calendar/CalendarBooking';
import {
  montanaHOAFormService,
  OnboardingFormData as MontanaOnboardingFormData,
} from '@/services/entities/hoa/montanaHOAFormService';
import type { OnboardingFormData } from '@/types/onboarding';
import type { CalendarBookingResponse } from '@/services/shared/calendarService';
import { Building, CheckCircle, ArrowRight, Eye, Clock, Award, Loader2 } from 'lucide-react';

// Step titles for progress indicator
const STEP_TITLES = [
  'Contact Info',
  'Financial Assessment',
  'Current State',
  'Vision & Solution',
  'Budget & Timeline',
  'Interests',
  'Recommendation',
];

const TOTAL_STEPS = 7;
const DEV_MODE_BYPASS_VALIDATION = true;

export const CommunityOnboardingPage: React.FC = () => {
  // Form state
  const [formData, setFormData] = useState<OnboardingFormData>({
    communityName: '',
    communityCity: '',
    communityState: '',
    communityLocation: '',
    totalUnits: '',
    yearEstablished: undefined,
    communityType: '',
    contactName: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    selectedTier: undefined,

    treasuryAmount: undefined,
    monthlyHOAFees: undefined,
    annualBudget: undefined,
    reserveFunds: undefined,
    outstandingDebts: undefined,
    investmentAccounts: false,
    bankingRelationships: [],
    customBankName: '',
    budgetStatus: '',

    currentGovernanceStructure: '',
    governance: [],
    governanceExperience: '',
    otherGovernanceStructure: '',
    timeCommitment: '',
    governanceComments: '',

    currentSoftwareSolutions: [],
    currentSoftware: [],
    currentSoftwareOther: [],
    otherSoftware: '',
    painPoints: [],
    previousFailures: '',
    additionalChallenges: '',
    biggestChallenges: [],
    painSeverity: '',
    integrationNeeds: [],

    currentFeatures: [],
    wishlistFeatures: [],
    featurePreferences: [],
    topPriority: '',
    customFeatureRequests: '',

    primaryGoals: [],
    goals: [],
    communityAssets: [],
    successMetrics: [],
    specificObjectives: '',
    complianceNeeds: [],
    seasonalNeeds: [],
    implementationTimeline: '',

    timelineExpectation: '',
    budgetRange: '',
    timeline: '',
    timelineDrivers: [],
    urgency: '',
    decisionProcess: '',
    competitiveAnalysis: '',
    decisionTimeline: '',
    evaluationCriteria: [],
    budgetFlexibility: '',
    budgetNotes: '',

    interests: [],
    progressiveInterests: [],
    communicationPreferences: [],
    engagementLevel: '',
    eventPreferences: [],
    additionalInterests: '',

    keyStakeholders: [],
    recommendedTier: undefined,
    recommendationReasoning: undefined,
    estimatedValue: undefined,
    formVersion: 'v2',
    requiresReview: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendarBooking, setShowCalendarBooking] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const { currentStep, navigation } = useOnboardingFlow();
  const progressiveDisclosure = useProgressiveDisclosure();
  const tierRecommendation = useTierRecommendation();

  const handleFieldChange = useCallback(
    (field: string, value: string | string[] | number | boolean | object) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[field]) delete newErrors[field];
        return newErrors;
      });
    },
    [],
  );

  const getValidationErrors = useCallback(() => {
    if (DEV_MODE_BYPASS_VALIDATION) return {};

    const newErrors: Record<string, string> = {};
    switch (currentStep) {
      case 1:
        if (!formData.contactName.trim()) newErrors.contactName = 'Name is required';
        if (!formData.contactEmail.trim()) newErrors.contactEmail = 'Email is required';
        if (!formData.communityName.trim()) newErrors.communityName = 'Community name is required';
        if (!formData.totalUnits) newErrors.totalUnits = 'Community size is required';
        if (!formData.communityType) newErrors.communityType = 'Community type is required';
        break;
      case 2:
        if (!formData.treasuryAmount) newErrors.treasuryAmount = 'Required';
        break;
      case 7:
        if (!formData.selectedTier) newErrors.selectedTier = 'Please select a package tier';
        break;
    }
    return newErrors;
  }, [currentStep, formData]);

  const validateCurrentStep = useCallback(() => {
    const newErrors = getValidationErrors();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [getValidationErrors]);

  // ==================== REFINED SUBMISSION LOGIC ====================
  const submitFormWithData = useCallback(async (dataToSubmit: OnboardingFormData) => {
    setIsSubmitting(true);
    try {
      console.log('🏔️ Submitting to Montana HOA form service');

      const hoaFormData: MontanaOnboardingFormData = {
        // Step 1: Basic info
        community_name: dataToSubmit.communityName || '',
        community_city: dataToSubmit.communityCity,
        community_state: dataToSubmit.communityState,
        community_location: dataToSubmit.communityLocation,
        total_units: String(dataToSubmit.totalUnits || ''),
        year_established: dataToSubmit.yearEstablished,
        community_type: dataToSubmit.communityType || '',
        contact_name: dataToSubmit.contactName || '',
        contact_email: dataToSubmit.contactEmail || '',
        contact_phone: dataToSubmit.contactPhone,
        contact_title: dataToSubmit.contactTitle,

        // Step 2: Financial
        treasury_amount: dataToSubmit.treasuryAmount
          ? String(dataToSubmit.treasuryAmount)
          : undefined,
        monthly_hoa_fees: dataToSubmit.monthlyHOAFees
          ? String(dataToSubmit.monthlyHOAFees)
          : undefined,
        annual_budget: dataToSubmit.annualBudget ? String(dataToSubmit.annualBudget) : undefined,
        reserve_funds: dataToSubmit.reserveFunds ? String(dataToSubmit.reserveFunds) : undefined,
        outstanding_debts: dataToSubmit.outstandingDebts
          ? String(dataToSubmit.outstandingDebts)
          : undefined,
        investment_accounts: dataToSubmit.investmentAccounts,
        banking_relationships: dataToSubmit.bankingRelationships,
        budget_status: dataToSubmit.budgetStatus,

        // Step 3: Governance & Current State
        governance: dataToSubmit.governance,
        governance_experience: dataToSubmit.governanceExperience,
        other_governance_structure: dataToSubmit.otherGovernanceStructure,
        time_commitment: dataToSubmit.timeCommitment,
        governance_comments: dataToSubmit.governanceComments,
        current_software: dataToSubmit.currentSoftware,
        other_software: dataToSubmit.otherSoftware,
        pain_points: dataToSubmit.painPoints,
        previous_failures: dataToSubmit.previousFailures,
        additional_challenges: dataToSubmit.additionalChallenges,
        pain_severity: dataToSubmit.painSeverity,
        integration_needs: dataToSubmit.integrationNeeds,

        // Step 4: Vision & Solution
        current_features: dataToSubmit.currentFeatures,
        wishlist_features: dataToSubmit.wishlistFeatures,
        top_priority: dataToSubmit.topPriority,
        custom_feature_requests: dataToSubmit.customFeatureRequests,
        goals: dataToSubmit.goals,
        community_assets: dataToSubmit.communityAssets,
        success_metrics: dataToSubmit.successMetrics,
        specific_objectives: dataToSubmit.specificObjectives,
        implementation_timeline: dataToSubmit.implementationTimeline,
        compliance_needs: dataToSubmit.complianceNeeds,
        seasonal_needs: dataToSubmit.seasonalNeeds,

        // Step 5: Budget & Timeline
        budget_range: dataToSubmit.budgetRange,
        current_budget: dataToSubmit.currentBudget,
        timeline: dataToSubmit.timeline,
        timeline_drivers: dataToSubmit.timelineDrivers,
        urgency: dataToSubmit.urgency,
        decision_process: dataToSubmit.decisionProcess,
        competitive_analysis: dataToSubmit.competitiveAnalysis,
        decision_timeline: dataToSubmit.decisionTimeline,
        evaluation_criteria: dataToSubmit.evaluationCriteria,
        budget_flexibility: dataToSubmit.budgetFlexibility,
        budget_notes: dataToSubmit.budgetNotes,
        decision_makers: dataToSubmit.decisionMakers,

        // Step 6: Interests & Engagement
        interests: dataToSubmit.interests,
        progressive_interests: dataToSubmit.progressiveInterests,
        communication_preferences: dataToSubmit.communicationPreferences,
        engagement_level: dataToSubmit.engagementLevel,
        event_preferences: dataToSubmit.eventPreferences,
        additional_interests: dataToSubmit.additionalInterests,

        // Extras
        heard_about_us: dataToSubmit.heardAboutUs,
        referrer_info: dataToSubmit.referrerInfo,
        willing_to_share: dataToSubmit.willingToShare,
        additional_info: dataToSubmit.additionalInfo,
        sales_notes: dataToSubmit.salesNotes,
        status: 'pending',

        // Recommendation
        recommended_tier: dataToSubmit.recommendedTier,
        recommendation_reasoning: dataToSubmit.recommendationReasoning,
        selected_tier: dataToSubmit.selectedTier,
        estimated_value: dataToSubmit.estimatedValue,
        form_version: 'v2-montana-consolidated',

        // Telemetry
        browser_fingerprint: dataToSubmit.browserFingerprint,
        device_info: dataToSubmit.deviceInfo,
        completion_time_seconds: dataToSubmit.completionTimeSeconds,
        last_activity_at: dataToSubmit.lastActivityAt,
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      };

      console.log('📦 [HOA API] Full Payload:', hoaFormData);
      const response = await montanaHOAFormService.submitOnboardingForm(hoaFormData);

      if (response.success) {
        setSubmissionComplete(true);
        setShowCalendarBooking(true);
      } else {
        throw new Error(response.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Submission failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setIsSubmitting(false);
    }
  }, []);

  const handleSubmission = useCallback(async () => {
    await submitFormWithData(formData);
  }, [formData, submitFormWithData]);

  const submitOnboarding = () => {
    if (validateCurrentStep()) handleSubmission();
  };

  const handleBookingComplete = useCallback(() => {}, []);
  const handleBookingError = useCallback(() => {}, []);
  const handleCloseCalendarBooking = useCallback(() => {
    setShowCalendarBooking(false);
    setIsSubmitting(false);
  }, []);

  // ==================== DEV MODE: FULL-FIDELITY AUTO-FILL ====================
  const autoFillAndSubmit = useCallback(async () => {
    console.log('🤖 [DEV MODE] EXECUTING HIGH-FIDELITY AUTO-FILL (7 STEPS)...');

    const highFidelityData: OnboardingFormData = {
      // Step 1
      communityName: '',
      communityCity: 'Glastonbury',
      communityState: 'CT',
      communityLocation: 'Glastonbury, Connecticut',
      totalUnits: '450',
      yearEstablished: 1985,
      communityType: 'hoa',
      contactName: 'Sarah Mitchell',
      contactTitle: 'Board President',
      contactEmail: 'sarah.mitchell@glastonbury-hoa.com',
      contactPhone: '(860) 555-0101',

      // Step 2
      treasuryAmount: 125000,
      monthlyHOAFees: 285,
      annualBudget: 650000,
      reserveFunds: 75000,
      outstandingDebts: 45000,
      investmentAccounts: true,
      bankingRelationships: ['First County Bank', 'Hartford Credit Union'],
      budgetStatus: 'Approved and stable',

      // Step 3
      governance: ['HOA Board + Management Company', 'Annual Member Meetings'],
      governanceExperience: '5+ years',
      timeCommitment: 'active',
      currentSoftware: ['QuickBooks', 'Excel/Google Sheets'],
      painPoints: ['Communication delays', 'Manual administrative tasks', 'Lack of transparency'],
      painSeverity: 'moderate',

      // Step 4
      goals: [
        'Improve Communication',
        'Reduce Administrative Overhead',
        'Enhance Financial Transparency',
      ],
      wishlistFeatures: [
        'Integrated Financial Management',
        'Advanced Communication Hub',
        'Digital Governance Tools',
      ],
      topPriority: 'Communication & Transparency',
      communityAssets: [
        { name: 'Swimming Pool(s)', category: 'Recreational Amenities', type: 'predefined' },
        { name: 'Clubhouse', category: 'Recreational Amenities', type: 'predefined' },
      ],
      successMetrics: ['Cost reduction/savings', 'Increased community engagement'],

      // Step 5
      budgetRange: 'standard',
      implementationTimeline: 'soon',
      urgency: 'high',
      decisionProcess: 'Board approval required',
      evaluationCriteria: ['Cost/ROI', 'Ease of Use', 'Security & Compliance'],
      budgetFlexibility: 'Somewhat flexible',

      // Step 6
      interests: ['Community Governance', 'Financial Management', 'Resident Services'],
      communicationPreferences: ['Email notifications', 'Mobile app push notifications'],
      engagementLevel: 'regular',

      // Meta
      selectedTier: 'gold',
      currentStep: 7,
      completedSteps: [1, 2, 3, 4, 5, 6, 7],
    };

    setFormData((prev) => ({ ...prev, ...highFidelityData }));
    await submitFormWithData(highFidelityData);
  }, [submitFormWithData]);

  const handleNext = () => {
    if (validateCurrentStep()) {
      navigation.nextStep();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === TOTAL_STEPS;
  const canProceed = Object.keys(getValidationErrors()).length === 0;

  const stepProps = {
    formData,
    errors,
    onFieldChange: handleFieldChange,
    progressiveDisclosure: {
      detectedInterests: progressiveDisclosure.detectedInterests,
      recommendedTier: progressiveDisclosure.recommendedTier,
      handleInterestSelection: progressiveDisclosure.handleInterestSelection,
      analyzeAndRecommendTier: progressiveDisclosure.analyzeAndRecommendTier,
      getRecommendationReasoning: progressiveDisclosure.getRecommendationReasoning,
    },
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1ContactInfo {...stepProps} />;
      case 2:
        return <Step2FinancialAssessment {...stepProps} />;
      case 3:
        return <Step3CurrentState {...stepProps} />;
      case 4:
        return <Step4VisionSolution {...stepProps} />;
      case 5:
        return <Step7BudgetTimeline {...stepProps} />;
      case 6:
        return <Step8InterestsEngagement {...stepProps} />;
      case 7:
        return <Step9Recommendation {...stepProps} />;
      default:
        return <Step1ContactInfo {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="border-b bg-white/95 backdrop-blur-sm supports-backdrop-filter:bg-white/60">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Building className="h-8 w-8 text-blue-600" />
              <span className="text-xl xs:text-2xl font-bold bg-linear-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate">
                Community App
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              {/* Dev auto-fill button removed for production. If you need it in dev,
                  enable it locally or restore the block guarded by import.meta.env.DEV */}
              <a href="/sales" className="text-slate-600 hover:text-blue-600 transition-colors">
                ← Back to Sales Page
              </a>
            </nav>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto mb-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl xs:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Community Onboarding
            </h1>
            <p className="text-lg xs:text-xl text-slate-600 max-w-3xl mx-auto px-2 xs:px-0">
              Help us understand your community so we can deliver a personalized demo.
            </p>
          </div>

          <div className="mb-8">
            <div className="relative">
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-slate-200">
                <div
                  style={{ width: `${((currentStep - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-linear-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out"
                />
              </div>
            </div>

            <div className="flex justify-between items-center px-2 xs:px-0">
              {STEP_TITLES.map((title, index) => {
                const stepNumber = index + 1;
                const isCompleted = stepNumber < currentStep;
                const isCurrent = stepNumber === currentStep;
                return (
                  <div
                    key={stepNumber}
                    className="flex flex-col items-center flex-1 max-w-[40px] xs:max-w-none"
                  >
                    <div
                      className={`w-6 h-6 xs:w-8 xs:h-8 rounded-full flex items-center justify-center text-xs xs:text-sm font-medium transition-all duration-300 ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? 'bg-blue-600 text-white ring-2 xs:ring-4 ring-blue-100' : 'bg-slate-200 text-slate-500'}`}
                    >
                      {isCompleted ? <CheckCircle className="w-3 h-3 xs:w-5 xs:h-5" /> : stepNumber}
                    </div>
                    <span
                      className={`mt-1 xs:mt-2 text-xs font-medium hidden sm:block text-center ${isCurrent ? 'text-blue-600' : 'text-slate-500'}`}
                    >
                      {title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="rounded-lg border bg-card text-card-foreground shadow-lg">
            <div className="p-4 xs:p-6 sm:p-8">
              {renderCurrentStep()}
              <div className="flex flex-col xs:flex-row justify-between items-center mt-8 xs:mt-12 pt-6 xs:pt-8 border-t space-y-4 xs:space-y-0">
                <button
                  onClick={navigation.prevStep}
                  disabled={isFirstStep}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border border-input bg-background hover:bg-slate-100 h-10 px-4 py-2 w-full xs:w-auto ${isFirstStep ? 'invisible' : ''}`}
                >
                  Previous
                </button>

                <div className="flex items-center space-x-1 xs:space-x-2 order-first xs:order-0">
                  {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 xs:w-3 xs:h-3 rounded-full ${i + 1 <= currentStep ? 'bg-blue-600' : 'bg-slate-200'}`}
                    />
                  ))}
                </div>

                {isLastStep ? (
                  <button
                    onClick={submitOnboarding}
                    disabled={isSubmitting || !canProceed}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium text-primary-foreground h-10 px-4 py-2 bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 w-full xs:w-auto"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit & Schedule Demo</span>
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium text-primary-foreground h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 w-full xs:w-auto"
                  >
                    Next Step <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CalendarBooking
        isOpen={showCalendarBooking}
        onClose={handleCloseCalendarBooking}
        userEmail={formData.contactEmail}
        userName={formData.contactName}
        onBookingComplete={handleBookingComplete}
        onBookingError={handleBookingError}
      />
    </div>
  );
};

export default CommunityOnboardingPage;
